import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const HERO_IMAGES = [
  '/hero/point3d-commercial-imaging-ltd-JbiLJnvj4b8-unsplash-55976649-1139-47d3-acf5-43c02843c3bd.png',
  '/hero/point3d-commercial-imaging-ltd-vkniNktOaIs-unsplash-410741c1-70b7-4d87-8b6f-8fe01dad3f7e.png',
  '/hero/francesca-tosolini-tHkJAMcO3QE-unsplash-c1d5a1b4-6f6f-4f50-86aa-f2c999b94aa7.png',
  '/hero/denike-adenaiya-isq5m4zBE8o-unsplash-ec6bc0fd-7b75-4c46-9554-ffdb38c91c20.png',
  '/hero/linda-gerbec-bKgcW7W-7hI-unsplash-d98d891f-ee5d-4aa0-80de-ff67d6fa776a.png',
  '/hero/walid-amghar-U6JLo_Meg8o-unsplash-0ab9696b-bbc2-44db-8ded-e9e69be46a5f.png',
  '/hero/shalev-cohen-KjueSklMNyY-unsplash-0086bb57-d9c6-478c-b883-61084e81d898.png',
  '/hero/allphoto-bangkok-nI4aC1kaTRc-unsplash-998f91e6-0138-4e20-880a-9864572dad7f.png',
  '/hero/joel-durkee-19PNqJiXC8g-unsplash-dcd116e4-5ffe-4929-b7fa-0be74a1598ea.png',
];

const ROTATE_INTERVAL_MS = 4500;

export default function Landing() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '85vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        margin: '0 -1.5rem',
        width: 'calc(100% + 3rem)',
      }}
    >
      {/* Auto-toggling hero background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
        }}
      >
        {HERO_IMAGES.map((src, index) => (
          <div
            key={src}
            style={{
              position: 'absolute',
              inset: 0,
              opacity: index === activeIndex ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              backgroundImage: `url(${src})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        ))}
        {/* Dark overlay for text readability */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
      </div>

      {/* Content */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          maxWidth: 720,
          margin: '0 auto',
          padding: '3rem 1.5rem',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
            fontWeight: 700,
            color: '#fff',
            marginBottom: '1rem',
            lineHeight: 1.2,
            textShadow: '0 2px 12px rgba(0,0,0,0.4)',
          }}
        >
          {t('landing.heroTitle')}
        </h1>
        <p
          style={{
            fontSize: '1.1rem',
            color: 'rgba(255,255,255,0.95)',
            marginBottom: '2rem',
            lineHeight: 1.6,
            textShadow: '0 1px 8px rgba(0,0,0,0.35)',
          }}
        >
          {t('landing.heroSubtitle')}
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link
            to="/listings"
            style={{
              display: 'inline-block',
              padding: '0.875rem 1.75rem',
              background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
              color: '#fff',
              borderRadius: 10,
              fontWeight: 600,
              fontSize: '1.05rem',
              textDecoration: 'none',
              boxShadow: '0 4px 14px rgba(13, 148, 136, 0.45)',
            }}
          >
            {t('landing.browseListings')}
          </Link>
          <Link
            to="/login"
            style={{
              display: 'inline-block',
              padding: '0.875rem 1.75rem',
              background: 'rgba(255,255,255,0.95)',
              color: '#0d9488',
              border: '2px solid transparent',
              borderRadius: 10,
              fontWeight: 600,
              fontSize: '1.05rem',
              textDecoration: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            }}
          >
            {t('landing.signIn')}
          </Link>
        </div>
      </div>

      {/* Carousel dots */}
      <div
        style={{
          position: 'absolute',
          bottom: '1.5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2,
          display: 'flex',
          gap: 8,
        }}
      >
        {HERO_IMAGES.map((_, index) => (
          <button
            key={index}
            type="button"
            aria-label={`Slide ${index + 1}`}
            onClick={() => setActiveIndex(index)}
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              background: index === activeIndex ? '#fff' : 'rgba(255,255,255,0.5)',
              boxShadow: index === activeIndex ? '0 0 0 2px rgba(0,0,0,0.2)' : 'none',
              transition: 'background 0.25s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}
